import { shelf } from './shelf'

/**
 * What the "With Ban" pages leave out of history, kept in the URL like every
 * other choice on this site: `bd` drivers, `bc` constructors, `bg` Grands Prix.
 */
export type Bans = { drivers: string[]; constructors: string[]; grandsPrix: string[] }

const KEYS = { drivers: 'bd', constructors: 'bc', grandsPrix: 'bg' } as const

export function readBans(params: URLSearchParams): Bans {
  const list = (key: string) => (params.get(key) ?? '').split(',').filter(Boolean)
  return {
    drivers: list(KEYS.drivers),
    constructors: list(KEYS.constructors),
    grandsPrix: list(KEYS.grandsPrix),
  }
}

/** `params` with `bans` written over its ban keys, everything else kept. */
export function writeBans(params: URLSearchParams, bans: Bans): URLSearchParams {
  const updated = new URLSearchParams(params)
  for (const [field, key] of Object.entries(KEYS) as [keyof Bans, string][]) {
    if (bans[field].length) updated.set(key, bans[field].join(','))
    else updated.delete(key)
  }
  return updated
}

/** The ban keys alone, as `bd=…&bg=…`: what the shelf keeps, and a `?…` without its `?`. */
export function banText(bans: Bans): string {
  return writeBans(new URLSearchParams(), bans).toString()
}

/** The ban keys alone, as a `?…` string a link can carry from page to page. */
export function banSearch(params: URLSearchParams): string {
  const only = banText(readBans(params))
  return only ? `?${only}` : ''
}

export function isEmpty(bans: Bans): boolean {
  return !bans.drivers.length && !bans.constructors.length && !bans.grandsPrix.length
}

/** The `/ban` query: nothing banned is no parameter at all. */
export function banQuery(bans: Bans) {
  return {
    ...(bans.drivers.length ? { exclude_drivers: bans.drivers.join(',') } : {}),
    ...(bans.constructors.length ? { exclude_constructors: bans.constructors.join(',') } : {}),
    ...(bans.grandsPrix.length ? { exclude_grands_prix: bans.grandsPrix.join(',') } : {}),
  }
}

// --- the shelf of named bans -------------------------------------------------

const SHELF = shelf('f1cal:bans')

/** Every saved set of bans, by name, each as its URL keys (`bd=…&bg=…`). */
export function listSavedBans(): Record<string, Bans> {
  return Object.fromEntries(
    Object.entries(SHELF.list()).map(([name, text]) => [
      name,
      readBans(new URLSearchParams(text)),
    ]),
  )
}

/** Saves under `name`, replacing bans of the same name. False when storage refused. */
export function saveBans(name: string, bans: Bans): boolean {
  return SHELF.save(name, banText(bans))
}

export const removeSavedBans = SHELF.remove
